/**
 * SnakeDraftBoardTicker - PGF Draft Board Sweep Item 1 (snake sibling).
 * Owns its own hook logic (chat ingestion, round-beat filler, dedup) -
 * snake has no nomination/soldInfo concept, so this cannot reuse
 * auction's DraftBoardChatTicker hooks directly. Renders through the
 * shared presentational <DraftMarquee>, which owns the release timing.
 *
 * [Snake Board Fixes item 5] Snake content is manager/LM chat plus
 * "ROUND N STARTING!" only. The "{team} picks {last}" filler was removed
 * - the board itself already shows every pick, so the line was noise
 * competing with live chat. Auction's own fillers are untouched.
 */
import { useEffect, useRef, useState } from "react";
import { useSnakeDraft } from "../../contexts/SnakeDraftContext";
import { personaNameColorFor } from "../../utils/personaColors";
import DraftMarquee from "../draft/DraftMarquee";

const DEAD_AIR_MS = 8000;
const COALESCE_WINDOW_MS = 500;

let _snakeTickerEntryId = 0;
const makeEntryId = () => `sktk_${Date.now()}_${_snakeTickerEntryId++}`;

export default function SnakeDraftBoardTicker({ isDark }) {
  const { draftState, chatMessages, roundStartedEvent } = useSnakeDraft();
  const managers = draftState?.managers || {};

  const [queue, setQueue] = useState([]);

  const seenChatIdsRef = useRef(new Set());
  const chatBaselineDoneRef = useRef(false);
  const seenFillerKeysRef = useRef(new Set());
  const lastExitAtRef = useRef(Date.now());
  const lastChatByManagerRef = useRef({});

  // Chat backfill guard - only surface messages arriving after mount
  // (SnakeDraftContext's hydrateChat backfills up to 100 on mount; the
  // ticker must not replay those). Mirrors auction ticker's §A.3.1 rule.
  useEffect(() => {
    if (chatBaselineDoneRef.current) return;
    (chatMessages || []).forEach((m) => {
      if (m?.id) seenChatIdsRef.current.add(m.id);
      else if (m?.timestamp) seenChatIdsRef.current.add(m.timestamp);
    });
    chatBaselineDoneRef.current = true;
  }, [chatMessages]);

  const pushEntry = (entry, coalesceKey) => {
    const now = Date.now();
    const enriched = { ...entry, id: makeEntryId() };
    setQueue((q) => [...q, enriched]);
    if (coalesceKey) {
      lastChatByManagerRef.current[coalesceKey] = { entryId: enriched.id, ts: now };
    }
  };

  const appendToEntry = (entryId, moreText) => {
    setQueue((q) => q.map((e) => (e.id === entryId ? { ...e, body: `${e.body} · ${moreText}` } : e)));
  };

  // ── Chat ingestion - manager chat only, no system/error rows ─────────
  useEffect(() => {
    if (!chatBaselineDoneRef.current) return;
    (chatMessages || []).forEach((m) => {
      const key = m?.id || m?.timestamp;
      if (!key || seenChatIdsRef.current.has(key)) return;
      seenChatIdsRef.current.add(key);
      if (m.is_system) return;
      const uid = m.sender_id;
      const mgr = uid ? managers[uid] : null;
      if (!mgr) return;
      const body = m.text || m.message_body || "";
      if (!body) return;
      const coalescePrev = lastChatByManagerRef.current[uid];
      const now = Date.now();
      if (coalescePrev && now - coalescePrev.ts < COALESCE_WINDOW_MS) {
        appendToEntry(coalescePrev.entryId, body);
        lastChatByManagerRef.current[uid] = { ...coalescePrev, ts: now };
        return;
      }
      pushEntry({
        type: "chat",
        teamName: (mgr.team_name || mgr.user_name || "").toUpperCase(),
        teamColor: personaNameColorFor(mgr, isDark),
        body,
      }, uid);
    });
  }, [chatMessages, managers, isDark]);

  const maybePushFiller = (key, entry) => {
    if (seenFillerKeysRef.current.has(key)) return;
    if (queue.length > 0) return;
    if (Date.now() - lastExitAtRef.current < DEAD_AIR_MS) return;
    seenFillerKeysRef.current.add(key);
    pushEntry(entry);
  };

  // ── Round-beat filler - "ROUND N STARTING!" on roundStartedEvent ────
  const bodyColor = isDark ? "#F5F5F5" : "#0A0A0A";
  useEffect(() => {
    if (!roundStartedEvent) return;
    maybePushFiller(`round_beat:${roundStartedEvent.round}:${roundStartedEvent.ts}`, {
      type: "filler_round",
      teamName: "",
      teamColor: bodyColor,
      body: `ROUND ${roundStartedEvent.round} STARTING!`,
      isFiller: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundStartedEvent?.round, roundStartedEvent?.ts]);

  const handleAnimationEnd = (entryId) => {
    lastExitAtRef.current = Date.now();
    setQueue((q) => q.filter((e) => e.id !== entryId));
  };

  return (
    <DraftMarquee
      queue={queue}
      onEntryAnimationEnd={handleAnimationEnd}
      isDark={isDark}
      testId="snake-draft-board-chat-ticker"
    />
  );
}
